import {
  Badge,
  Box,
  Button,
  Card,
  Center,
  Container,
  Group,
  Image,
  Skeleton,
  Stack,
  Text,
  Title,
  Tooltip,
} from "@mantine/core";
import {
  IconLayoutGrid,
  IconPlus,
  IconUser,
  IconWand,
} from "@tabler/icons-react";
import { useState } from "react";
import { Link } from "react-router-dom";
import type { Collection } from "@/api/collections";
import { CollectionFormModal } from "@/components/collections/CollectionFormModal";
import { useCollections } from "@/hooks/useCollections";
import { usePermissions } from "@/hooks/usePermissions";
import type { SeriesCondition } from "@/types/filters";
import { PERMISSIONS } from "@/types/permissions";
import {
  countRuleConditions,
  describesPersonalData,
} from "@/utils/collectionRules";

const NO_COVER =
  "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='200' height='300'%3E%3Crect fill='%23ddd' width='200' height='300'/%3E%3Ctext fill='%23999' font-family='sans-serif' font-size='14' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3ENo Cover%3C/text%3E%3C/svg%3E";

function CollectionCard({ collection }: { collection: Collection }) {
  const rule = collection.condition as SeriesCondition | null | undefined;
  const personalized = collection.automatic && describesPersonalData(rule);
  const ruleSize = collection.automatic ? countRuleConditions(rule) : 0;

  return (
    <Card
      component={Link}
      to={`/collections/${collection.id}`}
      shadow="sm"
      padding={0}
      radius="md"
      withBorder
      data-pressable="true"
      style={{ height: "100%", display: "flex", flexDirection: "column" }}
    >
      <Box style={{ aspectRatio: "150/212.125", overflow: "hidden" }}>
        <Image
          src={`/api/v1/collections/${collection.id}/thumbnail`}
          alt={collection.name}
          fit="cover"
          h="100%"
          fallbackSrc={NO_COVER}
        />
      </Box>
      <Stack gap={4} p="sm">
        <Text fw={600} size="sm" lineClamp={1}>
          {collection.name}
        </Text>
        {/* An automatic collection has no seriesCount: reporting one would mean
            resolving every rule to render this page. Describe the rule instead
            of rendering a misleading "0 series". */}
        <Text size="xs" c="dimmed">
          {collection.automatic
            ? ruleSize === 1
              ? "Automatic · 1 condition"
              : `Automatic · ${ruleSize} conditions`
            : `${collection.seriesCount ?? 0} series`}
        </Text>
        {collection.automatic && (
          <Group gap={4} wrap="wrap">
            <Tooltip label="Members come from a rule and stay up to date automatically">
              <Badge
                size="xs"
                variant="light"
                leftSection={<IconWand size={10} />}
              >
                Automatic
              </Badge>
            </Tooltip>
            {personalized && (
              <Tooltip label="This rule uses personal ratings or reading progress, so each person sees different series">
                <Badge
                  size="xs"
                  variant="light"
                  color="yellow"
                  leftSection={<IconUser size={10} />}
                >
                  Personal
                </Badge>
              </Tooltip>
            )}
          </Group>
        )}
      </Stack>
    </Card>
  );
}

export function Collections() {
  const { data: collections, isLoading } = useCollections();
  const { hasPermission } = usePermissions();
  const canWrite = hasPermission(PERMISSIONS.COLLECTIONS_WRITE);
  const [createOpen, setCreateOpen] = useState(false);

  return (
    <Container fluid py="md">
      <Group justify="space-between" align="center" mb="lg">
        <Group gap="xs">
          <IconLayoutGrid size={28} />
          <Title order={2}>Collections</Title>
        </Group>
        {canWrite && (
          <Button
            leftSection={<IconPlus size={16} />}
            onClick={() => setCreateOpen(true)}
          >
            New Collection
          </Button>
        )}
      </Group>

      {isLoading ? (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: "var(--mantine-spacing-md)",
          }}
        >
          {Array.from({ length: 6 }).map((_, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: static skeletons
            <Skeleton key={i} height={300} radius="md" />
          ))}
        </div>
      ) : !collections || collections.length === 0 ? (
        <Center mih={240}>
          <Stack align="center" gap="xs">
            <IconLayoutGrid size={48} opacity={0.4} />
            <Text c="dimmed">No collections yet.</Text>
            {canWrite && (
              <Text c="dimmed" size="sm">
                Create one, then add series to it from a series page.
              </Text>
            )}
          </Stack>
        </Center>
      ) : (
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))",
            gap: "var(--mantine-spacing-md)",
          }}
        >
          {collections.map((collection) => (
            <CollectionCard key={collection.id} collection={collection} />
          ))}
        </div>
      )}

      <CollectionFormModal
        opened={createOpen}
        onClose={() => setCreateOpen(false)}
      />
    </Container>
  );
}
